/**
 * API service for making authenticated HTTP requests to the Bili Engine
 * Separate from the main api.js which targets the Node server
 */

import logger from '../utils/logger';

import firebase from './firebase';

// Engine API configuration from environment variables
// Flask REST API runs on a different port from the WebSocket server
const ENGINE_BASE_URL = process.env.REACT_APP_ENGINE_URL;

/**
 * Get authentication token from Firebase
 */
async function getAuthToken() {
    const user = firebase.auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }
    return await user.getIdToken();
}

/**
 * Make an authenticated API request to the engine
 */
async function engineApiRequest(endpoint, options = {}) {
    try {
        const token = await getAuthToken();

        const response = await fetch(`${ENGINE_BASE_URL}${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...options.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({
                error: `HTTP error! status: ${response.status}`,
            }));
            throw new Error(
                error.error || `HTTP error! status: ${response.status}`
            );
        }

        // Handle 204 No Content
        if (response.status === 204) {
            return null;
        }

        return await response.json();
    } catch (error) {
        logger.error('Engine API request failed:', error);
        throw error;
    }
}

/**
 * Engine API methods for different HTTP verbs
 */
export const engineApi = {
    get: (endpoint) => engineApiRequest(endpoint, { method: 'GET' }),

    post: (endpoint, data) =>
        engineApiRequest(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    put: (endpoint, data) =>
        engineApiRequest(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    delete: (endpoint) => engineApiRequest(endpoint, { method: 'DELETE' }),
};

export default engineApi;
